export const parseDateValue = (value?: string | null) => {
  if (!value) return null;

  const dm = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (dm) {
    const dd = Number(dm[1]);
    const mm = Number(dm[2]);
    const yyyy = Number(dm[3]);
    if (!dd || !mm || !yyyy) return null;
    const date = new Date(yyyy, mm - 1, dd);
    if (Number.isNaN(date.getTime())) return null;
    if (date.getFullYear() !== yyyy || date.getMonth() !== mm - 1 || date.getDate() !== dd) return null;
    return date;
  }

  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const yyyy = Number(iso[1]);
    const mm = Number(iso[2]);
    const dd = Number(iso[3]);
    const date = new Date(yyyy, mm - 1, dd);
    if (Number.isNaN(date.getTime())) return null;
    if (date.getFullYear() !== yyyy || date.getMonth() !== mm - 1 || date.getDate() !== dd) return null;
    return date;
  }

  return null;
};

/** True while a typed DDMMYYYY prefix can still become a real day (01-31) and month (01-12). */
const isValidDatePrefix = (digits: string) => {
  const dd = digits.slice(0, 2);
  const mm = digits.slice(2, 4);
  if (dd.length === 1 && Number(dd) > 3) return false;
  if (dd.length === 2 && (Number(dd) < 1 || Number(dd) > 31)) return false;
  if (mm.length === 1 && Number(mm) > 1) return false;
  if (mm.length === 2 && (Number(mm) < 1 || Number(mm) > 12)) return false;
  return true;
};

/**
 * Formats typed digits as DD/MM/YYYY, adding the slashes automatically.
 * Digits that would make an impossible day or month are ignored, so "12251998" can't show a month of 25.
 */
export const formatDateInput = (raw: string) => {
  let digits = '';
  for (const digit of raw.replace(/\D/g, '').slice(0, 8)) {
    if (!isValidDatePrefix(digits + digit)) break;
    digits += digit;
  }
  const dd = digits.slice(0, 2);
  const mm = digits.slice(2, 4);
  const yyyy = digits.slice(4, 8);
  if (digits.length <= 2) return dd;
  if (digits.length <= 4) return `${dd}/${mm}`;
  return `${dd}/${mm}/${yyyy}`;
};

export const dateToDisplay = (value?: string | null) => {
  const parsed = parseDateValue(value);
  if (!parsed) return value || '';
  const dd = String(parsed.getDate()).padStart(2, '0');
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const yyyy = parsed.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

export const dateToIso = (value?: string | null) => {
  const parsed = parseDateValue(value);
  if (!parsed) return '';
  const yyyy = parsed.getFullYear();
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const dd = String(parsed.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const formatDateDisplay = (value?: string | null): string => {
  if (!value) return '--';
  const display = dateToDisplay(value);
  return display || '--';
};

export const todayDateDisplay = () => dateToDisplay(new Date().toISOString().split('T')[0]);

/** Age in whole years for a dd/mm/yyyy or yyyy-mm-dd date, or null when the date is invalid. */
export const getAgeInYears = (value?: string | null): number | null => {
  const dob = parseDateValue(value);
  if (!dob) return null;

  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : null;
};
